/**
 * ADR-086 — ATO session reports, formatted for a person.
 *
 * The diagnostic property bags in ar-diagnostics.ts are shaped for PostHog:
 * flat, scalar, one key per measurement, good for breakdowns and useless to
 * read. This module joins them into the handful of lines an operator actually
 * needs — device, what ARCore granted, how the session went — because that join
 * is the entire reason we route through our own service instead of pointing a
 * PostHog Slack destination at a channel.
 *
 * Pure: bags in, envelope out. The transport lives in messenger-client.ts.
 *
 * The envelope is ADR-086's, implemented locally rather than imported: the
 * package is deferred until a second consumer exists, so that the shape is
 * settled by two real callers rather than one imagined one. The contract is the
 * ADR; this is its first implementation.
 */

import type { WheelDetails } from '@/models/wheel-details'

export type MessengerSeverity = 'critical' | 'warning' | 'info'

export interface MessengerField {
  label: string
  value: string | number | boolean | null
}

export interface MessengerMessage {
  from: 'ato-android'
  to: 'ato-sessions'
  severity: MessengerSeverity
  kind: string
  title: string
  fields: MessengerField[]
  dedupeKey: string
  occurredAt: string
  /** Origin + path + query the session was launched from, as a clickable
   *  link. For AR that is the wheel code and env — enough to relaunch the
   *  exact session that produced the report. */
  sourceUrl?: string
  /** App Log (ADR-115): counted per brand and per origin, so the report names
   *  them as fields, not only inside the Wheel text. */
  brand?: string
  wheel?: string
  /** The storefront PDP the session was launched from. */
  parentUrl?: string
}

/** The subset of the capability probe a human wants named. */
interface CapabilityBag {
  device_model?: unknown
  android_version?: unknown
  chrome_version?: unknown
  ua_reduced?: unknown
}

type Bag = Record<string, unknown>

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null
}

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

/**
 * One readable device line.
 *
 * Chrome's User-Agent Reduction leaves model and OS version null on every
 * modern Android, so this has to degrade to whatever is actually known rather
 * than printing "null · null". When nothing resolves it says so, which is
 * itself a finding — it means the client hints were refused.
 */
function deviceLine(capability: CapabilityBag): string {
  const parts = [
    str(capability.device_model),
    str(capability.android_version) ? `Android ${str(capability.android_version)}` : null,
    str(capability.chrome_version) ? `Chrome ${str(capability.chrome_version)}` : null,
  ].filter((part): part is string => part !== null)
  return parts.length > 0 ? parts.join(' · ') : 'unidentified device'
}

/** Which of the tracked features the runtime refused.
 *
 *  Deliberately the refusals rather than the grants: the granted list is long
 *  and mostly uninteresting, so a normally-short refusal line is what makes an
 *  unusual one visible. Melih's Xiaomi granted everything and still could not
 *  track — legible only because "none" was the expected reading. */
const TRACKED_FEATURES: Array<[string, string]> = [
  ['feature_hit_test', 'hit-test'],
  ['feature_plane_detection', 'plane-detection'],
  ['feature_depth_sensing', 'depth-sensing'],
  ['feature_anchors', 'anchors'],
  ['feature_light_estimation', 'light-estimation'],
  ['feature_camera_access', 'camera-access'],
]

function refusedFeatures(started: Bag): string {
  const refused = TRACKED_FEATURES.filter(([key]) => started[key] === false).map(
    ([, name]) => name,
  )
  return refused.length === 0 ? 'none' : refused.join(', ')
}

/** Wheel + PDP lead the report when they resolved — "which wheel, whose
 *  page" is the first question asked of any AR finding, and until now the
 *  answer took a session-id lookup. Both are omitted (never dashed) when
 *  absent: details can lag the Start AR tap, and a QR scan has no PDP. */
function provenanceFields(input: {
  details?: WheelDetails | null
  pdpReferrer?: string | null
}): MessengerField[] {
  const fields: MessengerField[] = []
  if (input.details != null) {
    const name = `${input.details.brandName} ${input.details.modelName}`.trim()
    // The resolved size is the one on screen; append its diameter when known so
    // the report reads "Weld S70 · 20"". Omitted (no dangling " · ") otherwise.
    const diameter = input.details.assembledSize?.diameterInches ?? null
    const wheel = diameter != null ? `${name} · ${diameter}"` : name
    if (name !== '') fields.push({ label: 'Wheel', value: wheel })
  }
  if (input.pdpReferrer != null && input.pdpReferrer !== '') {
    fields.push({ label: 'PDP', value: input.pdpReferrer })
  }
  return fields
}

/** Brand, wheel and launching storefront, each only when known: details can
 *  lag the Start AR tap, and a QR scan has no PDP. */
function identity(input: { details?: WheelDetails | null; pdpReferrer?: string | null }) {
  return {
    ...(str(input.details?.brandName) ? { brand: input.details!.brandName } : {}),
    ...(str(input.details?.modelName) ? { wheel: input.details!.modelName } : {}),
    ...(str(input.pdpReferrer) ? { parentUrl: input.pdpReferrer! } : {}),
  }
}

export function buildSessionStartMessage(input: {
  sessionId: string
  environment: string
  capability: CapabilityBag
  started: Bag
  details?: WheelDetails | null
  pdpReferrer?: string | null
}): MessengerMessage {
  const depthUsage = str(input.started.depth_usage)
  const depthFormat = str(input.started.depth_data_format)
  return {
    from: 'ato-android',
    ...identity(input),
    to: 'ato-sessions',
    severity: 'info',
    kind: 'ar_session_started',
    title: 'AR session started',
    fields: [
      ...provenanceFields(input),
      { label: 'Device', value: deviceLine(input.capability) },
      { label: 'Refused', value: refusedFeatures(input.started) },
      {
        label: 'Depth',
        value:
          input.started.feature_depth_sensing === true
            ? `${depthUsage ?? 'usage?'} / ${depthFormat ?? 'format?'}`
            : input.started.depth_requested === true
              ? 'requested, not granted'
              : 'not requested',
      },
      { label: 'Session', value: input.sessionId },
    ],
    dedupeKey: `ar_session_started:${input.sessionId}`,
    occurredAt: new Date().toISOString(),
  }
}

/** "NotSupportedError: …" — Chrome's DOMException name is half the diagnosis
 *  (NotSupportedError = a required feature ARCore could not grant). */
function errorLine(error: unknown): string {
  if (!(error instanceof Error)) return String(error)
  return error.name && error.name !== 'Error' ? `${error.name}: ${error.message}` : error.message
}

/**
 * AR could not start at all (2026-10-30, ROH).
 *
 * The device that cannot start AR reports nothing else — no session means no
 * session events — so without this the failure lived only in PostHog, with no
 * device named and nothing in Slack. Warning, not critical: the usual cause is
 * the phone (ARCore missing, outdated or unsupported), not our code.
 */
export function buildSessionStartFailedMessage(input: {
  sessionId: string
  environment: string
  capability: CapabilityBag
  error: unknown
  details?: WheelDetails | null
  pdpReferrer?: string | null
}): MessengerMessage {
  return {
    from: 'ato-android',
    ...identity(input),
    to: 'ato-sessions',
    severity: 'warning',
    kind: 'ar_session_start_failed',
    title: 'AR session could not start',
    fields: [
      ...provenanceFields(input),
      { label: 'Device', value: deviceLine(input.capability) },
      { label: 'Error', value: errorLine(input.error) },
      { label: 'Session', value: input.sessionId },
    ],
    dedupeKey: `ar_session_start_failed:${input.sessionId}`,
    occurredAt: new Date().toISOString(),
  }
}

/** Percentage a person can judge at a glance. 6.6% reads as broken; 0.0657
 *  reads as nothing at all. */
function percent(value: number | null): string {
  return value === null ? '—' : `${Math.round(value * 100)}%`
}

function metres(value: number | null): string {
  return value === null ? '—' : `${value.toFixed(2)}m`
}

/** "2m 47s" / "42s" — a duration a person reads at a glance. */
function timeSpent(seconds: number): string {
  const s = Math.max(0, Math.round(seconds))
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`
}

/**
 * The session ended (2026-10-21, developer).
 *
 * Always info, never a tracking verdict: people stop pointing the camera at
 * anything useful before they tap the X, so a verdict taken at the end called
 * nearly every session degraded. A real degradation is reported while the
 * session is still running (buildTrackingDegradedMessage). This says how long
 * the shopper spent and whether they took a photo — the two things the end of a
 * session can actually tell us.
 */
export function buildSessionSummaryMessage(input: {
  sessionId: string
  environment: string
  capability: CapabilityBag
  summary: Bag
  /** Photos saved this session (successful captures only). */
  photosCaptured: number
  details?: WheelDetails | null
  pdpReferrer?: string | null
}): MessengerMessage {
  return {
    from: 'ato-android',
    ...identity(input),
    to: 'ato-sessions',
    severity: 'info',
    kind: 'ar_session_summary',
    title: 'AR session ended',
    fields: [
      ...provenanceFields(input),
      { label: 'Device', value: deviceLine(input.capability) },
      { label: 'Time spent', value: timeSpent(num(input.summary.session_duration_s) ?? 0) },
      { label: 'Photo', value: input.photosCaptured > 0 ? `${input.photosCaptured} captured` : 'none' },
      { label: 'Session', value: input.sessionId },
    ],
    dedupeKey: `ar_session_summary:${input.sessionId}`,
    occurredAt: new Date().toISOString(),
  }
}

/**
 * Tracking has been degraded for `degradedForS` while the session is still
 * running (2026-10-21). The only tracking report: sent once per session, by
 * createSustainedDegradedDetector, with the diagnostics that used to ride the
 * end-of-session summary.
 *
 * A warning, not an alert: nothing is on fire, but a device could not map the
 * scene for a real stretch of a live session and somebody should look. The
 * verdict goes in the TITLE because the title is the Slack notification.
 */
export function buildTrackingDegradedMessage(input: {
  sessionId: string
  environment: string
  capability: CapabilityBag
  summary: Bag
  degradedForS: number
  details?: WheelDetails | null
  pdpReferrer?: string | null
}): MessengerMessage {
  const health = str(input.summary.tracking_health) ?? 'unknown'
  const planeCount = num(input.summary.plane_count) ?? 0
  const wheels = num(input.summary.wheels_placed) ?? 0

  const depthFailure = str(input.summary.depth_last_failure)
  const depthLine = [
    `${metres(num(input.summary.depth_last_m))} vs plane ${metres(
      num(input.summary.plane_hit_last_m),
    )}`,
    depthFailure ? `· ${depthFailure}` : null,
  ]
    .filter((part): part is string => part !== null)
    .join(' ')

  return {
    from: 'ato-android',
    ...identity(input),
    to: 'ato-sessions',
    severity: 'warning',
    kind: 'ar_tracking_degraded',
    title: `AR tracking degraded for ${input.degradedForS}s — session still running`,
    fields: [
      ...provenanceFields(input),
      { label: 'Device', value: deviceLine(input.capability) },
      {
        label: 'Tracking',
        value: `${health} · ${planeCount} planes · ${percent(
          num(input.summary.depth_yield),
        )} depth yield`,
      },
      {
        label: 'Hit tests',
        value: `${num(input.summary.hit_test_frames_with_result) ?? 0} landed / ${
          num(input.summary.hit_test_frames_empty) ?? 0
        } empty`,
      },
      {
        label: 'Anchors',
        value: `${num(input.summary.anchors_created) ?? 0} created / ${
          num(input.summary.anchors_landed) ?? 0
        } landed · drift ${metres(num(input.summary.anchor_drift_m))}`,
      },
      { label: 'Depth', value: depthLine },
      {
        label: 'Outcome',
        value: `${wheels} wheels placed · floor ${
          input.summary.floor_found === true ? 'found' : 'not found'
        }`,
      },
      { label: 'Into session', value: timeSpent(num(input.summary.session_duration_s) ?? 0) },
      { label: 'Session', value: input.sessionId },
    ],
    dedupeKey: `ar_tracking_degraded:${input.sessionId}`,
    occurredAt: new Date().toISOString(),
  }
}

/**
 * The payload carried no wheel size at all (2026-09-07).
 *
 * The header pill shows "Model | 17"" from the payload size and the
 * pinch snaps against it; with no size the segment is hidden and the
 * pinch degrades to continuous. Both degrade silently on the phone —
 * this notice is what makes the catalog gap visible to a person.
 *
 * Returns null when a size IS derivable (assembled size, or any entry
 * in the size list) — the caller sends only what comes back.
 */
export function buildWheelSizeMissingMessage(input: {
  details: WheelDetails
  environment: string
}): MessengerMessage | null {
  const { details } = input
  const hasSize =
    details.assembledSize != null || details.sizes.length > 0
  if (hasSize) return null
  const part = details.partID
  return {
    from: 'ato-android',
    ...identity(input),
    to: 'ato-sessions',
    severity: 'warning',
    kind: 'wheel_size_missing',
    title: 'Wheel payload carries no size — header size hidden',
    fields: [
      {
        label: 'Wheel',
        value: `${details.brandName} ${details.modelName}`.trim(),
      },
      { label: 'Part', value: part },
      { label: 'Environment', value: input.environment },
    ],
    // Keyed on the part, not the session: one wheel with a missing
    // size is one gap, however many sessions open it.
    dedupeKey: `wheel_size_missing:${part ?? 'unknown'}`,
    occurredAt: new Date().toISOString(),
  }
}
